import { AbstractExitReason, AbstractUser, Reason } from "@kvix/shared";
import { Button, Text } from "@kvix/ui";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  FormHelperText,
  Input,
  makeStyles,
  MenuItem,
  Select,
  TextareaAutosize,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { KvixUserContext } from "../../../../../../contexts/user";

const useStyles = makeStyles((theme) => ({
  dialogContainer: {
    textAlign: "center",
    padding: "38px",
    margin: "auto",
    width: "100%",
  },
  dialogTitle: {
    whiteSpace: "pre-line",
    letterSpacing: "0",
    lineHeight: "44px",
  },
  dialogText: {
    whiteSpace: "pre-line",
    wordBreak: "break-word",
  },
  dialogActions: {
    justifyContent: "center",
    flexDirection: "column",
    padding: "0 10%",
  },
  dialogButton: {
    width: "100%",
    margin: "0 0 10px 0",
  },
  dialogInput: {
    fontWeight: 500,
    letterSpacing: "0.5px",
    background: theme.palette.type === "dark" ? "#425784" : "#FEE9EE",
    height: "44px",
    width: "100%",
    lineHeight: "24px",
  },
  dialogReasonTextArea: {
    resize: "none",
    fontFamily: "inherit",
    padding: "12px",
    fontSize: "14px",
    fontWeight: 500,
    letterSpacing: "0.5px",
    background: "#FEE9EE",
    border: 0,
    borderRadius: "8px",
    marginBottom: "16px",
    outline: "none",
  },
  dialogButtonClose: {
    width: "100%",
  },
}));

export const DeleteUserDialog: React.FC<{
  open: boolean;
  user: AbstractUser;
  fullScreen: boolean;
  handleOpen: (value: boolean) => void;
}> = (props) => {
  const classes = useStyles(props);
  const [email, setEmail] = useState("");
  const emailMatches = email === props.user.email;
  const formError = email.length > 0 && !emailMatches;
  const [userRemovalStatus, setUserRemovalStatus] = useState(null);
  const { actions } = useContext(KvixUserContext);
  const { t, ready } = useTranslation(["account", "general"]);
  const [userRemovalReason, setUserRemovalReason] = useState<Reason>(
    Reason.None
  );
  const [userRemovalOtherReason, setUserRemovalOtherReason] = useState("");

  const removeProfile = async (email: string) => {
    if (props.user.email === email) {
      try {
        setUserRemovalStatus("loading");
        const exitReason: Partial<AbstractExitReason> = {
          reason: userRemovalReason,
          reasonText: userRemovalOtherReason,
        };
        await actions.removeUser(exitReason);
        setUserRemovalStatus("success");
      } catch (error) {
        setUserRemovalStatus("failed");
      }
    }
  };

  const closeDialog = () => {
    if (userRemovalStatus === "success") actions.signOut();
    props.handleOpen(false);
    setEmail("");
  };

  const handleChangeRemovalReason = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = (event.target as HTMLInputElement).value;
    const cancelReason: Reason =
      Reason[value.charAt(0).toUpperCase() + value.slice(1)];
    setUserRemovalReason(cancelReason);
    setUserRemovalOtherReason("");
  };

  const templateRemovalReasons: {
    id: string;
    label: string;
  }[] = t("account.deleteAccountDialog.deleteCancelReasons", {
    returnObjects: true,
  });

  if (!ready) {
    return null;
  }

  return (
    <Dialog
      open={props.open}
      onClose={() => closeDialog()}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullScreen={props.fullScreen}
    >
      <div className={classes.dialogContainer}>
        {userRemovalStatus === "success" ? (
          <>
            <Text className={classes.dialogTitle} variant="h3">
              {t("account.deleteAccountDialog.deletedTitle")}
            </Text>
            <DialogContent style={{ margin: "12px 0" }}>
              <DialogContentText className={classes.dialogText}>
                {t("account.deleteAccountDialog.deletedSubtitle")}
              </DialogContentText>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button
                className={classes.dialogButton}
                onClick={() => actions.signOut()}
                color="primary"
              >
                {t("general:buttons.close")}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <Text className={classes.dialogTitle} variant="h3">
              {t("account.deleteAccountDialog.title")}
            </Text>
            <DialogContent style={{ margin: "12px 0" }}>
              <DialogContentText
                className={classes.dialogText}
                hidden={props.user.email ? false : true}
              >
                {t("account.deleteAccountDialog.subtitle")}
                <b>{` ${props.user.email}`}</b>
              </DialogContentText>
            </DialogContent>
            <DialogActions className={classes.dialogActions} disableSpacing>
              <FormControl style={{ width: "100%" }} error={formError}>
                {props.user.email && (
                  <>
                    <Input
                      type="email"
                      color="primary"
                      disableUnderline
                      autoFocus
                      className={classes.dialogInput}
                      placeholder={t(
                        "account.deleteAccountDialog.inputPlaceholder"
                      )}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                    {emailMatches ? (
                      <FormHelperText
                        style={{
                          margin: "-2px 0px 18px 4px",
                          color: "green",
                        }}
                      >
                        {t("account.deleteAccountDialog.inputValid")}
                      </FormHelperText>
                    ) : (
                      <FormHelperText
                        style={{
                          margin: "-2px 0px 18px 4px",
                        }}
                      >
                        {t("account.deleteAccountDialog.inputInvalid")}
                      </FormHelperText>
                    )}
                  </>
                )}
              </FormControl>
              <FormControl style={{ width: "100%" }} error={formError}>
                <Select
                  fullWidth
                  onChange={handleChangeRemovalReason}
                  value={userRemovalReason}
                  variant="outlined"
                  style={{ textAlign: "start", marginBottom: "16px" }}
                  required
                >
                  {templateRemovalReasons
                    .filter((r) => r.id !== "manualPayment")
                    .map((reason) => (
                      <MenuItem key={reason.id} value={reason.id}>
                        {reason.label}
                      </MenuItem>
                    ))}
                </Select>

                {userRemovalReason !== "none" && (
                  <TextareaAutosize
                    className={classes.dialogReasonTextArea}
                    placeholder={
                      userRemovalReason === "other"
                        ? t("account.deleteAccountDialog.enterOtherReasonText")
                        : t("account.deleteAccountDialog.enterReasonText")
                    }
                    rowsMin={3}
                    rowsMax={5}
                    color="primary"
                    onChange={(event) =>
                      setUserRemovalOtherReason(event.target.value)
                    }
                  />
                )}
                {userRemovalOtherReason.length > 200 && (
                  <Alert severity="error" style={{ marginBottom: "16px" }}>
                    {t("account.deleteAccountDialog.reasonTextError")}
                  </Alert>
                )}
                <Button
                  onClick={() => removeProfile(email)}
                  color="primary"
                  variant="contained"
                  disabled={
                    !emailMatches ||
                    userRemovalReason === "none" ||
                    userRemovalOtherReason.length > 200
                  }
                  className={classes.dialogButton}
                  loading={userRemovalStatus === "loading"}
                  id="confirmdelete"
                >
                  {t("general:buttons.delete")}
                </Button>
                <Button
                  onClick={() => closeDialog()}
                  color="primary"
                  variant="text"
                  className={classes.dialogButtonClose}
                >
                  {t("general:buttons.cancel")}
                </Button>
              </FormControl>
            </DialogActions>
            {userRemovalStatus === "failed" && (
              <Alert severity="error">
                {t("general:errors.somethingWrong")}
              </Alert>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};
